import {
  DefaultError,
  InfiniteData,
  QueryKey,
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { DocumentSnapshot } from "firebase/firestore";
import { TablePaginatedData, TableSortModel } from "shared/types/tables";

type QueryFnData<TData> = {
  items: TablePaginatedData<TData>;
  totalPages: number;
};

type PageParam = {
  startListItem: DocumentSnapshot | null;
  endListItem: DocumentSnapshot | null;
};

type PaginationQueryParams<TData> = Omit<
  UndefinedInitialDataInfiniteOptions<
    QueryFnData<TData>,
    DefaultError,
    InfiniteData<QueryFnData<TData>>,
    QueryKey,
    PageParam
  >,
  "initialPageParam" | "getNextPageParam" | "getPreviousPageParam"
> & {
  limit: number;
  sortModel?: TableSortModel;
  page: {
    currentPage: number;
    prevPage: number;
  };
};

export const usePaginationQuery = <TData>(
  params: PaginationQueryParams<TData>,
) => {
  const { limit, sortModel, page, ...useQueryParams } = params;

  return useInfiniteQuery<
    QueryFnData<TData>,
    DefaultError,
    InfiniteData<QueryFnData<TData>>,
    QueryKey,
    PageParam
  >({
    ...useQueryParams,
    queryKey: [...useQueryParams.queryKey, limit, sortModel],
    initialPageParam: {
      startListItem: null,
      endListItem: null,
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.totalPages === allPages.length) {
        return null;
      }

      return {
        startListItem: null,
        endListItem: lastPage.items.endListItem,
      };
    },
    getPreviousPageParam: (firstPage, allPages) => {
      if (
        page.currentPage === page.prevPage ||
        page.currentPage > page.prevPage ||
        firstPage.totalPages === allPages.length
      ) {
        return null;
      }

      return {
        startListItem: firstPage.items.startListItem,
        endListItem: null,
      };
    },
  });
};
